import { useEffect, useRef } from 'react';
import { onInstant } from '@/shared/api/websocket/realtime-sync.registry';
import {
  payloadObject,
  payloadString,
  payloadBool,
} from '@/shared/api/websocket/realtime-sync.payload';
import { useUser } from '@/app/model/stores';
import type { ValidationItem } from './types';
import { decodeValidation, type UploadRowCopy } from './decode-validation';

interface UseValidationListenerProps {
  setValidationItems: React.Dispatch<React.SetStateAction<ValidationItem[]>>;
  /** Fallback row text when the server sent none. */
  copy: UploadRowCopy;
}

export function useValidationListener({
  setValidationItems,
  copy,
}: UseValidationListenerProps) {
  const user = useUser();

  // Read, never re-subscribed on — see useValidationPolling.
  const copyRef = useRef(copy);
  copyRef.current = copy;

  useEffect(() => {
    if (!user) return;

    // The intake announces itself once, with the outcome already settled
    // (docs/upload/ADR-002) — there is no earlier event to guard against.
    const cleanup = onInstant('uploads', (event) => {
      if (event.action !== 'completed') return;
      if (!event.id) return;

      const validation = payloadObject(event.payload, 'validation');
      if (!validation) return;

      const status = payloadString(validation, 'status');
      if (!status) return;

      const outcome = payloadObject(event.payload, 'outcome');
      const outcomeType = outcome ? payloadString(outcome, 'type') : null;
      const angleId =
        outcomeType === 'angle' && outcome
          ? (payloadString(outcome, 'id') ?? undefined)
          : undefined;

      // One decoder for all three validation sources (initial / poll / WS) —
      // handles the lenient-vs-strict different-building split and every routed
      // outcome. See decode-validation.ts.
      const decoded = decodeValidation(
        {
          status,
          recommendations: payloadString(validation, 'recommendations'),
          routed_to_documents:
            payloadBool(validation, 'routed_to_documents') === true,
          routed_to_references:
            payloadBool(validation, 'routed_to_references') === true,
          routed_to_output_style:
            payloadBool(validation, 'routed_to_output_style') === true,
          validation_failure_reason: payloadString(
            validation,
            'validation_failure_reason'
          ),
          can_override_building:
            payloadBool(validation, 'can_override_building') === true,
          auto_accepted_as_view:
            payloadBool(validation, 'auto_accepted_as_view') === true,
        },
        copyRef.current
      );

      setValidationItems((prev) =>
        prev.map((item) =>
          item.id === event.id
            ? {
                ...item,
                status: decoded.status,
                note: decoded.note,
                error: decoded.error,
                validation: decoded.validation,
                angleId,
              }
            : item
        )
      );
    });

    return cleanup;
  }, [user, setValidationItems]);
}
